import { BrowserRouter, Routes, Route } from "react-router";
import { LandingLayout } from "./features/landing/layout";
import { DashboardLayout } from "./features/dashboard/layout";
import { SessionProvider } from "./services/auth0/session";
import LandingScreen from "./features/landing/landing.screen";
import DashboardScreen from "./features/dashboard/dashboard.screen";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Auth0ProviderWithNavigate } from "./services/auth0/auth";
import { OnboardingLayout } from "./features/onboarding/layout";
import { GoogleMapsApiProvider } from "./services/maps/google-maps-api-provider";
import OnboardingSuccessful from "./features/onboarding/onboarding-successful";
import { ProtectedLayout } from "./features/protected/layout";
import ProfileScreen from "./features/profile/profile.screen";
import { ProfileLayout } from "./features/profile/layout";
import BusinessAccountScreen from "./features/profile/business-account.screen";
import AddressScreen from "./features/profile/address.screen";
import InvoicesScreen from "./features/invoices/invoices.screen";
import { InvoicesLayout } from "./features/invoices/layout";
import OnboardingStep1Screen from "./features/onboarding/onboarding-step-1.screen";
import OnboardingStep2Screen from "./features/onboarding/onboarding-step-2.screen";
import CreateOrderScreen from "./features/orders/create-order.screen";
import EditOrderScreen from "./features/orders/edit-order.screen";
import { OrdersLayout } from "./features/orders/layout";
import BillingScreen from "./features/profile/billing.screen";
import PrivacyPolicyScreen from "./features/legal/privacy-policy.screen";
import TermsOfUseScreen from "./features/legal/terms-of-use.screen";
import LegalLayout from "./features/legal/layout";
import { TrackingRoute } from "./features/tracking/tracking-route";
import PaymentMethodsScreen from "./features/profile/payment-method.screen";
import DynamicTitle from "./components/dynamic-title";
import SettingsScreen from "./features/profile/settings.screen";
import { ErrorLayout } from "./features/errors/layout";
import UnauthorizedScreen from "./features/errors/unauthorized.screen";
import NotFoundScreen from "./features/errors/not-found.screen";
import { Toaster } from "@/components/ui/toaster";

const queryClient = new QueryClient();

const SocketProvider = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

export const Router = () => {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools buttonPosition="bottom-left" />
        <Toaster />
        <Auth0ProviderWithNavigate>
          <SessionProvider>
            <SocketProvider>
              <GoogleMapsApiProvider>
                <DynamicTitle />
                <Routes>
                  <Route element={<LandingLayout />}>
                    <Route path="/" element={<LandingScreen />} />
                  </Route>
                  <Route element={<LegalLayout />}>
                    <Route
                      path="/privacy-policy"
                      element={<PrivacyPolicyScreen />}
                    />
                    <Route
                      path="/terms-of-use"
                      element={<TermsOfUseScreen />}
                    />
                  </Route>
                  <Route element={<OnboardingLayout />}>
                    <Route
                      path="/onboarding/step-1"
                      element={<OnboardingStep1Screen />}
                    />
                    <Route
                      path="/onboarding/step-2"
                      element={<OnboardingStep2Screen />}
                    />
                    <Route
                      path="/onboarding/success"
                      element={<OnboardingSuccessful />}
                    />
                  </Route>

                  <Route element={<OrdersLayout />}>
                    <Route
                      path="/orders/create"
                      element={<CreateOrderScreen />}
                    />
                    <Route
                      path="/orders/edit/:orderId"
                      element={<EditOrderScreen />}
                    />
                  </Route>
                  <Route element={<ProfileLayout />}>
                    <Route path="/settings" element={<SettingsScreen />} />
                    <Route path="/account" element={<ProfileScreen />} />
                    <Route
                      path="/business-account"
                      element={<BusinessAccountScreen />}
                    />
                    <Route path="/billing" element={<BillingScreen />} />
                    <Route
                      path="/payment-methods"
                      element={<PaymentMethodsScreen />}
                    />
                  </Route>
                  <Route element={<ProtectedLayout />}>
                    <Route element={<DashboardLayout />}>
                      <Route path="/dashboard" element={<DashboardScreen />} />
                    </Route>
                    <Route element={<InvoicesLayout />}>
                      <Route path="/invoices" element={<InvoicesScreen />} />
                    </Route>
                    <Route
                      path="/my-address-book"
                      element={<AddressScreen />}
                    />
                  </Route>
                  <Route
                    path="/tracking/:trackingNumber"
                    element={<TrackingRoute />}
                  />

                  {/* Error routes */}
                  <Route element={<ErrorLayout />}>
                    <Route
                      path="/unauthorized"
                      element={<UnauthorizedScreen />}
                    />
                    {/* Catch all route for 404 */}
                    <Route path="*" element={<NotFoundScreen />} />
                  </Route>
                </Routes>
              </GoogleMapsApiProvider>
            </SocketProvider>
          </SessionProvider>
        </Auth0ProviderWithNavigate>
      </QueryClientProvider>
    </BrowserRouter>
  );
};
